import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { TOS_VERSION_CURRENT } from "@/lib/partner";
import { useLocale } from "@/i18n/useLocale";
import content from "@/data/partner_public_content.json";

// Public partner Terms. Version is the same string the API expects on
// POST /api/partner/apply. Bump `TOS_VERSION_CURRENT` in lib/partner.js AND
// PARTNER_TOS_VERSION in backend/models/partner.py together on any material
// change so existing partners can be re-prompted at next apply.
//
// Wave 2 addition: bilingual notice banner + tier ladder enumeration are added
// on ALL locales (informational, not conditional). The English legal body
// remains in English pending legal review.

const Section = ({ h, children }) => (
  <section className="mt-6" data-testid={`tos-section-${(h || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}>
    <h2 className="text-lg font-medium text-white">{h}</h2>
    <div className="mt-2 space-y-2 text-sm leading-relaxed text-white/65">{children}</div>
  </section>
);

export default function PartnerTerms() {
  const { isRtl } = useLocale();
  // We show BOTH strings side-by-side (informational banner is bilingual on all
  // locales). Ladder labels are bilingual too. Neither depends on runtime locale.
  const ladderAr = content.ar.terms.tierLadder;
  const ladderEn = content.en.terms.tierLadder;

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="aria-dark relative min-h-screen overflow-hidden bg-[#0B0D12] text-white"
      style={{ fontFamily: "'Outfit', sans-serif" }}
      data-testid="partner-terms-page"
    >
      <div className="grain-overlay" style={{ opacity: 0.05 }} />
      <div className="relative mx-auto max-w-2xl px-6 py-16">
        <Link to="/partners/apply" className="inline-flex items-center gap-2 text-sm text-white/60 transition-colors hover:text-white" data-testid="partner-terms-back">
          <ArrowLeft className={`h-4 w-4 ${isRtl ? "rotate-180" : ""}`} /> Back to application
        </Link>

        {/* Wave 2: bilingual language-availability notice — shown on all locales. */}
        <div
          className="mt-6 rounded-2xl border border-[#D6A653]/30 bg-[#D6A653]/8 p-4"
          data-testid="partner-terms-language-notice"
        >
          <p dir="rtl" className="text-right text-sm text-[#F0CD84]" data-testid="partner-terms-language-notice-ar">
            {content.ar.terms.notice}
          </p>
          <p className="mt-1 text-xs text-white/55" data-testid="partner-terms-language-notice-en">
            {content.en.terms.notice}
          </p>
        </div>

        {/* Wave 2: tier ladder enumeration — bilingual, all locales. */}
        <div
          className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5"
          data-testid="partner-terms-tier-ladder"
        >
          <p className="text-[11px] uppercase tracking-wider text-white/45">
            {ladderEn.title} · {ladderAr.title}
          </p>
          <p className="mt-2 text-xs text-white/50">{ladderEn.sub}</p>
          <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="partner-terms-tier-list">
            {ladderEn.rates.map((row, i) => (
              <li
                key={row.labelEn}
                className="flex items-baseline justify-between gap-3 rounded-xl border border-white/8 bg-[#0D1015] px-3 py-2"
                data-testid={`partner-terms-tier-${row.labelEn.toLowerCase()}`}
              >
                <div className="min-w-0">
                  <p className="text-sm text-white">
                    {row.labelEn} <span className="text-white/40">·</span>{" "}
                    <span dir="rtl" className="text-white/80">{row.labelAr}</span>
                  </p>
                  <p className="mt-0.5 text-[11px] text-white/45">
                    {row.seats} · <span dir="rtl">{ladderAr.rates[i]?.seats}</span>
                  </p>
                </div>
                <p className="shrink-0 text-lg font-light text-[#D6A653] tabular-nums">
                  {row.rate}
                  <span className="ml-1 text-[10px] uppercase tracking-wider text-white/40">
                    {ladderEn.recurring}
                  </span>
                </p>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-8">
          <div className="flex items-center gap-3">
            <img src="/tp-mark.png" alt="TapPresence" className="h-6 w-6 object-contain" />
            <span className="text-[15px] font-semibold tracking-tight">TapPresence</span>
          </div>
          <h1 className="mt-6 text-3xl font-medium tracking-tight text-white" data-testid="partner-terms-title">
            Partner Program Terms
          </h1>
          <p className="mt-1 text-xs text-white/40" data-testid="partner-terms-version">
            Version <span className="font-mono">{TOS_VERSION_CURRENT}</span>
          </p>

          <Section h="1. Eligibility">
            You must be a legally registered individual or entity, at least eighteen years of age
            (or the age of majority in your jurisdiction, whichever is greater), and in good
            standing on the TapPresence platform to participate in the Partner Program. TapPresence
            reserves the right to refuse or revoke Program membership at its discretion, including
            where you are located in a jurisdiction to which TapPresence cannot lawfully send
            commissions.
          </Section>

          <Section h="2. Commission structure">
            Commissions are calculated on the invoice-paid amount (excluding tax, refunds and
            processing fees) for each active paid subscription attributed to you. Commission rates
            begin at 15% and scale up to 30% based on the total active-seat count attributed to
            your account, refreshed automatically. Rates apply on a going-forward basis — moving
            up a tier does not retroactively re-price previously-earned commissions.
          </Section>

          <Section h="3. Attribution & tracking">
            Attribution is recorded via your unique referral link or an issued partner discount
            code. The referral cookie is last-touch within a ninety (90) day window: if a visitor
            arrives through your link and later, within that window, arrives through another
            partner&rsquo;s link, the most recent partner receives attribution. Once a workspace pays
            its first invoice under an attribution, that attribution is locked to that partner
            for the life of that subscription.
          </Section>

          <Section h="4. Self-referral & code use">
            Self-referral is prohibited. You may not use your own referral link or partner discount
            code on a workspace you own, control, or operate on behalf of yourself. Attribution
            recorded from a self-referral will be stripped and the associated commission voided.
            Repeated self-referral attempts are grounds for suspension.
          </Section>

          <Section h="5. Discount code use">
            Partner discount codes cannot be combined with other promotional codes at checkout.
            Only one code applies per checkout. You may not misrepresent a partner discount as an
            official TapPresence sale or public promotion, and you may not publish partner codes
            on public coupon-aggregation sites.
          </Section>

          <Section h="6. Payout terms">
            Payouts are issued on a monthly cycle for commissions in &ldquo;approved&rdquo; status once the
            balance per partner per currency exceeds the minimum payout threshold ($50 USD or
            equivalent). You are responsible for providing accurate payout details. TapPresence is
            not liable for payouts sent to details you have provided in error and reserves the
            right to hold payouts pending verification where fraud is suspected.
          </Section>

          <Section h="7. Clawback policy">
            Commissions on invoices that are subsequently refunded, disputed by the customer, or
            written off as uncollectible are reversed automatically — there is no arbitrary
            &ldquo;safe&rdquo; window. If the underlying commission has already been paid to you, a clawback
            entry is recorded and the balance is deducted from your next payout, or invoiced back
            to you where legally required.
          </Section>

          <Section h="8. Suspension & termination">
            TapPresence may suspend or terminate your partner account at any time for a violation
            of these Terms, of the TapPresence Terms of Service, of applicable law, or in
            TapPresence&rsquo;s reasonable judgment to protect the platform, customers or the brand.
            Approved-but-unpaid commissions accrued prior to suspension for cause may be withheld
            pending review.
          </Section>

          <Section h="9. IP & trademark use">
            TapPresence grants you a limited, non-exclusive, non-transferable, revocable licence
            to use the TapPresence name and marks solely to promote the service in accordance with
            any brand guidelines TapPresence publishes from time to time. All goodwill in the
            marks accrues to TapPresence. You may not register any domain, handle, or other
            identifier that is confusingly similar to the TapPresence marks, or bid on
            TapPresence-branded keywords in paid search except where TapPresence expressly
            authorises it in writing.
          </Section>

          <Section h="10. Non-solicitation">
            You will not solicit TapPresence customers or employees to leave TapPresence for a
            competing product or role for the duration of your participation in the Program and
            for a reasonable period thereafter.
          </Section>

          <Section h="11. Confidentiality">
            You will treat any non-public information you receive from TapPresence (including
            partner-portal analytics, commission data, unreleased pricing, and roadmap details) as
            confidential and will not disclose it except as required by law.
          </Section>

          <Section h="12. Compliance & data protection">
            You will comply with all applicable laws in your promotion of TapPresence, including
            anti-spam laws (CAN-SPAM, CASL, GDPR e-privacy), advertising standards, consumer
            protection regulation, and data protection regulation. You will not misrepresent
            TapPresence&rsquo;s features, pricing, or availability.
          </Section>

          <Section h="13. Amendments">
            TapPresence may amend these Terms at any time. Material changes will be communicated
            by email or in-portal at least fourteen (14) days before taking effect. Continued
            participation in the Program constitutes acceptance of the amended Terms. The
            currently-accepted version is stored on your partner account.
          </Section>

          <Section h="14. Contact">
            Questions about these Terms, your account, or a specific commission should be sent to
            {" "}
            <a href="mailto:partners@tappresence.com" className="text-[#D6A653] underline underline-offset-2" data-testid="partner-terms-support-email">partners@tappresence.com</a>.
          </Section>
        </div>
      </div>
    </div>
  );
}
